import { Environment } from '../../types';

export type PDFOfferCurrency = 'USD' | 'GBP' | 'EUR';

/**
 * Offer id → `PLATFORM DIGITAL PRODUCT` name, copied from the backend admin
 * (`Select digital product offer to change` list).
 *
 * The offers were created in a different order on prod and on staging, so the ids
 * do NOT match between the environments — ids 1..4 and 11..14 differ. Keep both maps
 * in sync with the admin whenever a new offer is added.
 */
const PROD_PDF_OFFERS: Record<number, string> = {
  1: 'everydoggy_PDF_adult_19_99_USD',
  2: 'everydoggy_PDF_puppy_19_99_USD',
  3: 'everydoggy_PDF_adult_29_99_USD',
  4: 'everydoggy_PDF_puppy_29_99_USD',
  5: 'everydoggy_PDF_puppy_00_99_USD',
  6: 'everydoggy_PDF_adult_00_99_USD',
  7: 'everydoggy_PDF_adult_19_99_GBP',
  8: 'everydoggy_PDF_puppy_19_99_GBP',
  9: 'everydoggy_PDF_adult_19_99_EUR',
  10: 'everydoggy_PDF_puppy_19_99_EUR',
  11: 'everydoggy_PDF_adult_29_99_GBP',
  12: 'everydoggy_PDF_adult_29_99_EUR',
  13: 'everydoggy_PDF_puppy_29_99_GBP',
  14: 'everydoggy_PDF_puppy_29_99_EUR',
  15: 'everydoggy_PDF_puppy_39_99_USD',
  16: 'everydoggy_PDF_puppy_39_99_GBP',
  17: 'everydoggy_PDF_puppy_39_99_EUR',
  18: 'everydoggy_PDF_adult_39_99_USD',
  19: 'everydoggy_PDF_adult_39_99_GBP',
  20: 'everydoggy_PDF_adult_39_99_EUR',
  21: 'everydoggy_PDF_K9_19_99_USD',
  22: 'everydoggy_PDF_K9_19_99_GBP',
  23: 'everydoggy_PDF_K9_19_99_EUR',
};

const TEST_PDF_OFFERS: Record<number, string> = {
  1: 'everydoggy_PDF_puppy_19_99_USD',
  2: 'everydoggy_PDF_adult_19_99_USD',
  3: 'everydoggy_PDF_puppy_29_99_USD',
  4: 'everydoggy_PDF_adult_29_99_USD',
  5: 'everydoggy_PDF_puppy_00_99_USD',
  6: 'everydoggy_PDF_adult_00_99_USD',
  7: 'everydoggy_PDF_adult_19_99_GBP',
  8: 'everydoggy_PDF_puppy_19_99_GBP',
  9: 'everydoggy_PDF_adult_19_99_EUR',
  10: 'everydoggy_PDF_puppy_19_99_EUR',
  11: 'everydoggy_PDF_puppy_29_99_GBP',
  12: 'everydoggy_PDF_adult_29_99_EUR',
  13: 'everydoggy_PDF_puppy_29_99_EUR',
  14: 'everydoggy_PDF_adult_29_99_GBP',
  15: 'everydoggy_PDF_puppy_39_99_USD',
  16: 'everydoggy_PDF_puppy_39_99_GBP',
  17: 'everydoggy_PDF_puppy_39_99_EUR',
  18: 'everydoggy_PDF_adult_39_99_USD',
  19: 'everydoggy_PDF_adult_39_99_GBP',
  20: 'everydoggy_PDF_adult_39_99_EUR',
  21: 'everydoggy_PDF_K9_19_99_USD',
  22: 'everydoggy_PDF_K9_19_99_GBP',
  23: 'everydoggy_PDF_K9_19_99_EUR',
};

const PDF_OFFERS = process.env.ENVIRONMENT === Environment.prod ? PROD_PDF_OFFERS : TEST_PDF_OFFERS;

const PDF_OFFER_ID_BY_NAME: Record<string, number> = {};
for (const id in PDF_OFFERS) {
  PDF_OFFER_ID_BY_NAME[PDF_OFFERS[id]] = Number(id);
}

export const getInternalPDFId = (id: number) => PDF_OFFERS[id];

/** `product_key` of the K9 Reactivity Field Manual — one offer per currency, 19.99. */
export const K9_PRODUCT_KEY = 'k9_guide';

/** `product_key` → the guide part of the `PLATFORM DIGITAL PRODUCT` name. */
const GUIDE_SLUG_BY_PRODUCT_KEY: Record<string, string> = {
  puppy_guide: 'puppy',
  adult_dog_guide: 'adult',
  [K9_PRODUCT_KEY]: 'K9',
};

/**
 * `PLATFORM DIGITAL PRODUCT` name of a guide for the given price and currency —
 * the same value `getInternalPDFId` returns, built without knowing the offer id.
 */
export const buildInternalPDFId = (
  productKey: string,
  price: string,
  currency: PDFOfferCurrency,
): string => {
  const guide = GUIDE_SLUG_BY_PRODUCT_KEY[productKey] ?? 'adult';
  const priceSlug = Number(price) < 1 ? '00_99' : price.replace('.', '_');

  return `everydoggy_PDF_${guide}_${priceSlug}_${currency}`;
};

/**
 * Offer id of a guide for the given price and currency, or `undefined` when that
 * combination isn't in the catalog of the current environment.
 */
export const getPDFOfferId = (
  productKey: string,
  price: string,
  currency: PDFOfferCurrency,
): number | undefined => PDF_OFFER_ID_BY_NAME[buildInternalPDFId(productKey, price, currency)];
